import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import {
	Dialog,
	EmojiPicker,
	Form,
	FormItem,
	IconButton,
	AutogrowTextArea,
	TextAreaHandle,
	ValidationError,
	renderReactDialog,
	Toolbar
} from "client/Components";
import {
	makeBirthdayWish,
	MakeBirthdayWishModel,
} from "client/Api/Birthdays/birthdayWish";
import { localize } from "client_legacy/ndaw/culture";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	AlertManager,
	useAlerts,
	useIsTouchDevice
} from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import styles from "./MakeBirthdayWishDialog.module.less";
import { IAttachableTextInputPropsWithBuilder } from "client/Forms/attachableProps/textInput";
import classNames from "classnames";

const validationConfiguration: IValidationConfiguration<MakeBirthdayWishModel> = createValidationConfiguration<MakeBirthdayWishModel>();

validationConfiguration.forScalar("birthdayWish")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

export type MakeBirthdayWishDialogProps = {
	firstName: string;
	lastName: string;
	userId: number;
	onClose(): void;
	onSend(): void;
};

export const MakeBirthdayWishDialog = (props: MakeBirthdayWishDialogProps): ReactElement => {
	const {
		firstName,
		lastName,
		userId,
		onClose,
		onSend
	} = props;

	const [isInteractingWithTheBackend, setIsInteractingWithTheBackend] = useState<boolean>(false);
	const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);
	const birthdayWishTextAreaRef: RefObject<TextAreaHandle | null> = useRef<TextAreaHandle>(null);
	const emojiButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const isTouch: boolean = useIsTouchDevice();

	const alertManager: AlertManager = useAlerts();

	const fullname: string = `${firstName} ${lastName}`;

	const [model, setModel] = useState<MakeBirthdayWishModel>({
		birthdayWish: localize("_HappyBirthdayX").replace("{0}", fullname),
		toUserId: userId
	});

	const formHandler: IFormHandler<MakeBirthdayWishModel> = useFormHandler<MakeBirthdayWishModel>(
		model,
		setModel,
		validationConfiguration
	);

	const insertEmojiHandler = useCallback(
		(emojiUnicode: string): void => {
			birthdayWishTextAreaRef.current
				?.insert(emojiUnicode);
		},
		[]
	);

	const sendHandler = async (): Promise<void> => {
		const validationResult: InputValidationResult = await formHandler.validation.validate();
		if (validationResult.kind === InputValidationResultKind.Invalid) {
			return;
		}

		try {
			setIsInteractingWithTheBackend(true);

			await makeBirthdayWish(model);

			onSend();
		} catch (e) {
			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
			setIsInteractingWithTheBackend(false);
		}
	};

	const birthdayWishAttachable: IAttachableTextInputPropsWithBuilder<string> = formHandler.for("birthdayWish")
		.attachToTextArea();

	const isValid: boolean = birthdayWishAttachable.validation
		?.isValid() ?? false;

	return (
		<Dialog
			trackingId="make-birthday-wish-dialog"
			hasBusyIndicator={isInteractingWithTheBackend}
		>
			<Dialog.Header
				title={localize("_SendBirthdayWishes")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={sendHandler}>
					<FormItem>
						<div
							className={
								classNames(
									styles.birthdayWishContainer,
									{
										[styles.birthdayWishContainer_hasError]: !isValid
									}
								)
							}
						>
							<div className={styles.birthdayWishInputWrapper}>
								<AutogrowTextArea
									{...birthdayWishAttachable}
									autoFocus
									isBorderless
									hasNoPadding
									ref={birthdayWishTextAreaRef}
								/>
							</div>
							{!isTouch && (
								<div className={styles.emojiButtonContainer}>
									<IconButton
										ref={emojiButtonRef}
										icon="face-smile"
										kind="transparent-colored"
										size="large"
										shape="round"
										onClick={(): void => setIsEmojiPickerOpen(value => !value)}
										trackingId="insert-emoji"
										isActive={isEmojiPickerOpen}
										tooltip={localize("_Emoji")}
									/>
								</div>
							)}
						</div>
						{isEmojiPickerOpen
							&& isNotNil(emojiButtonRef.current)
							&& !isTouch
							&& (
								<EmojiPicker
									target={emojiButtonRef.current}
									onClose={(): void => setIsEmojiPickerOpen(false)}
									onSelected={insertEmojiHandler}
									trackingId="emoji-picker"
								/>
							)
						}
						<ValidationError {...formHandler.for("birthdayWish").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Send")}
						onClick={sendHandler}
						trackingId="request"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
MakeBirthdayWishDialog.displayName = "MakeBirthdayWishDialog";

export function showMakeBirthdayWishDialog(props: Omit<MakeBirthdayWishDialogProps, "onClose">): void {
	const {
		onSend,
		...restOfTheProps
	} = props;

	const createSendHandler = (onClose: () => void): (() => void) => {
		return () => {
			onSend();
			onClose();
		};
	};
	renderReactDialog(
		onClose => (
			<MakeBirthdayWishDialog
				{...restOfTheProps}
				onSend={createSendHandler(onClose)}
				onClose={onClose}
			/>
		)
	);
}